import { useState } from "react";
import { toast } from "react-toastify";
import { useFetch } from "../../../custom/api";
import ChallengeDayScroller from "./ChallengeDayScroller";
import DailyCheckInButton from "./DailyCheckInButton";
import ChallengeCompletionCard from "./ChallengeCompletionCard";
import { notifyUnlocked } from "../../../components/achievements/AchievementUnlockToast";
import { toFarsiNumber } from "../../../custom/helpers";
import { formatJalali } from "../jalaliDate";
import "../../../styles/Challenges.css";

// Renders a participant's live state for one challenge - the same component
// is used embedded in the Home banner and on the Challenge details
// page; days are shown even before the challenge starts.
function ChallengeProgress({ challenge, participation, progress, onChanged }) {
  const { myFetch } = useFetch();
  const [busy, setBusy] = useState(false);

  if (!participation || !progress) return null;

  const handleCheckin = async (status) => {
    setBusy(true);
    try {
      const resp = await myFetch(`challenges/${challenge.challenge_id}/checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const r = await resp.json();
      if (r.status === "success") {
        if (status === "done" && !r.already_checked_in) {
          toast.success(
            r.completed_now ? "چالش رو کامل کردی! 🎉" : `${r.points_awarded}+ امتیاز گرفتی`
          );
        }
        if (r.newly_unlocked && r.newly_unlocked.length > 0) {
          notifyUnlocked(r.newly_unlocked);
        }
        onChanged && onChanged();
      } else {
        toast.error(r.message || "مشکلی پیش اومد، دوباره امتحان کن");
      }
    } catch (e) {
      toast.error("مشکلی توی ثبت پیش اومد، دوباره امتحان کن");
    } finally {
      setBusy(false);
    }
  };

  if (participation.state === "completed") {
    return (
      <ChallengeCompletionCard challengeId={challenge.challenge_id} participation={participation} />
    );
  }

  const days = progress.days || [];
  const total = challenge.duration_days || days.length;
  const doneCount = days.filter((d) => d.state === "completed").length;
  const waiting = participation.state === "waiting_for_start";
  const dayNow = progress.today_day_number || progress.focus_day_number;

  let daysUntil = null;
  if (waiting && days.length) {
    const start = new Date(`${days[0].date}T00:00:00`);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    daysUntil = Math.max(1, Math.round((start - now) / 86400000));
  }

  return (
    <div className="challengeProgressCard challengeLive">
      <div className="spc-between centerItemAlign">
        <p className="heading-3 rightAlign">{challenge.title}</p>
        {waiting ? (
          <p className="challengePointsPill subject-2">
            {daysUntil ? `${toFarsiNumber(daysUntil)} روز تا شروع` : "به‌زودی"}
          </p>
        ) : (
          <p className="challengePointsPill subject-2">{toFarsiNumber(participation.points)} امتیاز</p>
        )}
      </div>

      <p className="grey-2 caption-1 rightAlign marginTop5 yekan">
        {waiting
          ? `شروع چالش: ${toFarsiNumber(formatJalali(days[0] ? days[0].date : participation.start_date, "dddd DD MMMM"))}`
          : `روز ${toFarsiNumber(dayNow)} از ${toFarsiNumber(total)}`}
      </p>

      <div className="challengeMeter" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={doneCount}>
        <span style={{ width: `${total ? Math.round((doneCount / total) * 100) : 0}%` }} />
      </div>
      <div className="spc-between subject-2 grey-2 yekan">
        <span>{toFarsiNumber(doneCount)} از {toFarsiNumber(total)} روز انجام شده</span>
        {!waiting && <span>{toFarsiNumber(total - doneCount)} روز مونده</span>}
      </div>

      <div className="challengeDayTray">
        <ChallengeDayScroller days={progress.days} focusDayNumber={progress.focus_day_number} />
      </div>

      {participation.today_checkin_status === "today" && (
        <DailyCheckInButton onCheckin={handleCheckin} busy={busy} />
      )}
      {participation.today_checkin_status === "completed" && (
        <p className="challengeDoneLabel yekan">امروز ثبت شد ✓</p>
      )}
      {participation.today_checkin_status === "missed" && (
        <p className="grey-2 caption-1 centerAlign marginTop5 yekan">
          برای امروز ثبت شد. فردا دوباره امتحان کن!
        </p>
      )}
      {participation.state === "ended_incomplete" && (
        <p className="grey-2 caption-1 centerAlign marginTop5 yekan">
          مدت چالش تموم شده؛ پیشرفتت توی لیدربورد باقی می‌مونه.
        </p>
      )}
    </div>
  );
}

export default ChallengeProgress;
